/**
 * 模板建组合 data layer (docs/tech/12). Every request names the draft's shop in
 * X-Shop-Id: the API treats a draft / execution of another shop as not found, and
 * the shop picked in step ① may differ from the ambient current shop.
 */
import type {
  TemplateCategoryCandidates,
  TemplateKeywordClassifyTask,
  TemplateKeywordGenerateInput,
  TemplateKeywordGenerateTask,
  TemplateKeywordGenerateTasks,
  TemplatePortfolioDraft,
  TemplatePortfolioDraftSettings,
  TemplatePortfolioDraftUpdateInput,
  TemplatePortfolioExecution,
  TemplatePortfolioImportInput,
  TemplatePortfolioPreview,
} from "@adpilot/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

interface Envelope<T> {
  success: true;
  data: T;
}

const headers = (shopId: string) => ({ "X-Shop-Id": shopId });
const draftPath = (draftId: string) => `template-portfolio/drafts/${draftId}`;
const executionPath = (executionId: string) => `template-portfolio/executions/${executionId}`;

async function get<T>(shopId: string, path: string): Promise<T> {
  return (await api.get(path, { headers: headers(shopId) }).json<Envelope<T>>()).data;
}

async function send<T>(
  method: "post" | "patch",
  shopId: string,
  path: string,
  json?: unknown,
): Promise<T> {
  return (await api[method](path, { headers: headers(shopId), json }).json<Envelope<T>>()).data;
}

/** 本店品牌 is derived server-side from the product; the client never sends it (Q8). */
export type TemplatePortfolioEditableSettings = Omit<TemplatePortfolioDraftSettings, "ownBrand">;

export type TemplatePortfolioDraftPatch = Omit<TemplatePortfolioDraftUpdateInput, "settings"> & {
  settings?: TemplatePortfolioEditableSettings;
};

export function editableSettings(
  settings: TemplatePortfolioDraftSettings | TemplatePortfolioEditableSettings,
): TemplatePortfolioEditableSettings {
  const { ownBrand: _ownBrand, ...rest } = settings as TemplatePortfolioDraftSettings;
  return rest;
}

// ── Plain functions ─────────────────────────────────────────────

export const createTemplatePortfolioDraft = (
  shopId: string,
  settings?: TemplatePortfolioEditableSettings,
) =>
  send<TemplatePortfolioDraft>("post", shopId, "template-portfolio/drafts", {
    settings: settings && editableSettings(settings),
  });

export const fetchTemplatePortfolioDraft = (shopId: string, draftId: string) =>
  get<TemplatePortfolioDraft>(shopId, draftPath(draftId));

export const updateTemplatePortfolioDraft = (
  shopId: string,
  draftId: string,
  input: TemplatePortfolioDraftPatch,
) =>
  send<TemplatePortfolioDraft>("patch", shopId, draftPath(draftId), {
    ...input,
    settings: input.settings && editableSettings(input.settings),
  });

export const importTemplatePortfolioKeywords = (
  shopId: string,
  draftId: string,
  input: TemplatePortfolioImportInput,
) => send<TemplatePortfolioDraft>("post", shopId, `${draftPath(draftId)}/imports`, input);

export const previewTemplatePortfolioDraft = (shopId: string, draftId: string) =>
  send<TemplatePortfolioPreview>("post", shopId, `${draftPath(draftId)}/preview`);

export const fetchTemplateCategoryCandidates = (shopId: string, draftId: string) =>
  get<TemplateCategoryCandidates>(shopId, `${draftPath(draftId)}/category-candidates`);

export const startTemplateKeywordClassify = (shopId: string, draftId: string) =>
  send<TemplateKeywordClassifyTask>("post", shopId, `${draftPath(draftId)}/classify`);

export const retryTemplateKeywordClassify = (shopId: string, draftId: string) =>
  send<TemplateKeywordClassifyTask>("post", shopId, `${draftPath(draftId)}/classify/retry`);

export const fetchTemplateKeywordClassifyTask = (shopId: string, draftId: string) =>
  get<TemplateKeywordClassifyTask>(shopId, `${draftPath(draftId)}/classify`);

export const startTemplateKeywordGenerate = (
  shopId: string,
  draftId: string,
  input: TemplateKeywordGenerateInput,
) => send<TemplateKeywordGenerateTask>("post", shopId, `${draftPath(draftId)}/generate`, input);

export const fetchTemplateKeywordGenerateTasks = (shopId: string, draftId: string) =>
  get<TemplateKeywordGenerateTasks>(shopId, `${draftPath(draftId)}/generate`);

export const executeTemplatePortfolioDraft = (
  shopId: string,
  draftId: string,
  expectedVersion: number,
) =>
  send<TemplatePortfolioExecution>("post", shopId, `${draftPath(draftId)}/execute`, {
    expectedVersion,
  });

export const fetchTemplatePortfolioExecution = (shopId: string, executionId: string) =>
  get<TemplatePortfolioExecution>(shopId, executionPath(executionId));

export const retryTemplatePortfolioExecutionItem = (
  shopId: string,
  executionId: string,
  itemId: string,
) =>
  send<TemplatePortfolioExecution>(
    "post",
    shopId,
    `${executionPath(executionId)}/items/${itemId}/retry`,
  );

export const enableTemplatePortfolioExecution = (shopId: string, executionId: string) =>
  send<TemplatePortfolioExecution>("post", shopId, `${executionPath(executionId)}/enable`);

export const retryTemplatePortfolioEnableItem = (
  shopId: string,
  executionId: string,
  itemId: string,
) =>
  send<TemplatePortfolioExecution>(
    "post",
    shopId,
    `${executionPath(executionId)}/items/${itemId}/enable/retry`,
  );

// ── Query keys ──────────────────────────────────────────────────

export const templatePortfolioKeys = {
  draft: (draftId: string) => ["template-portfolio", "draft", draftId] as const,
  preview: (draftId: string, version: number) =>
    ["template-portfolio", "draft", draftId, "preview", version] as const,
  categoryCandidates: (draftId: string) =>
    ["template-portfolio", "draft", draftId, "category-candidates"] as const,
  classify: (draftId: string) => ["template-portfolio", "draft", draftId, "classify"] as const,
  generate: (draftId: string) => ["template-portfolio", "draft", draftId, "generate"] as const,
  execution: (executionId: string) => ["template-portfolio", "execution", executionId] as const,
};

const POLL_MS = 2_000;
const ACTIVE_AI = new Set(["queued", "running"]);

// ── Queries ─────────────────────────────────────────────────────

export function useTemplatePortfolioDraft(shopId: string, draftId: string | undefined) {
  return useQuery({
    queryKey: templatePortfolioKeys.draft(draftId ?? ""),
    queryFn: () => fetchTemplatePortfolioDraft(shopId, draftId as string),
    enabled: Boolean(draftId),
  });
}

export function useTemplateCategoryCandidates(shopId: string, draftId: string) {
  return useQuery({
    queryKey: templatePortfolioKeys.categoryCandidates(draftId),
    queryFn: () => fetchTemplateCategoryCandidates(shopId, draftId),
    staleTime: 5 * 60_000,
  });
}

/** Polls while the task is queued / running. */
export function useTemplateKeywordClassifyTask(shopId: string, draftId: string) {
  return useQuery({
    queryKey: templatePortfolioKeys.classify(draftId),
    queryFn: () => fetchTemplateKeywordClassifyTask(shopId, draftId),
    refetchInterval: (query) =>
      ACTIVE_AI.has(query.state.data?.status ?? "idle") ? POLL_MS : false,
  });
}

/** Polls while any pool's generation is queued / running. */
export function useTemplateKeywordGenerateTasks(shopId: string, draftId: string) {
  return useQuery({
    queryKey: templatePortfolioKeys.generate(draftId),
    queryFn: () => fetchTemplateKeywordGenerateTasks(shopId, draftId),
    refetchInterval: (query) =>
      Object.values(query.state.data ?? {}).some((task) => ACTIVE_AI.has(task.status))
        ? POLL_MS
        : false,
  });
}

export function isTemplatePortfolioExecutionActive(execution: TemplatePortfolioExecution): boolean {
  return (
    execution.status === "pending" ||
    execution.status === "running" ||
    execution.counts.enablePending > 0 ||
    execution.counts.enabling > 0
  );
}

/** Polls while creation or 一键开启 is in progress. */
export function useTemplatePortfolioExecution(shopId: string, executionId: string | undefined) {
  return useQuery({
    queryKey: templatePortfolioKeys.execution(executionId ?? ""),
    queryFn: () => fetchTemplatePortfolioExecution(shopId, executionId as string),
    enabled: Boolean(executionId),
    refetchInterval: (query) =>
      query.state.data && isTemplatePortfolioExecutionActive(query.state.data) ? POLL_MS : false,
  });
}

// ── Mutations ───────────────────────────────────────────────────
// Callers handle onError (toast / field errors); successful draft writes refresh the
// draft cache so every step reads the latest version.

function useDraftWrite<TVariables>(
  mutationFn: (variables: TVariables) => Promise<TemplatePortfolioDraft>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (draft) => queryClient.setQueryData(templatePortfolioKeys.draft(draft.id), draft),
  });
}

export function useCreateTemplatePortfolioDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      shopId,
      settings,
    }: {
      shopId: string;
      settings?: TemplatePortfolioEditableSettings;
    }) => createTemplatePortfolioDraft(shopId, settings),
    onSuccess: (draft) => queryClient.setQueryData(templatePortfolioKeys.draft(draft.id), draft),
  });
}

export function useUpdateTemplatePortfolioDraft(shopId: string, draftId: string) {
  return useDraftWrite((input: TemplatePortfolioDraftPatch) =>
    updateTemplatePortfolioDraft(shopId, draftId, input),
  );
}

export function useImportTemplatePortfolioKeywords(shopId: string, draftId: string) {
  return useDraftWrite((input: TemplatePortfolioImportInput) =>
    importTemplatePortfolioKeywords(shopId, draftId, input),
  );
}

export function useStartTemplateKeywordClassify(shopId: string, draftId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ retry = false }: { retry?: boolean } = {}) =>
      retry
        ? retryTemplateKeywordClassify(shopId, draftId)
        : startTemplateKeywordClassify(shopId, draftId),
    onSuccess: (task) => queryClient.setQueryData(templatePortfolioKeys.classify(draftId), task),
  });
}

export function useStartTemplateKeywordGenerate(shopId: string, draftId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TemplateKeywordGenerateInput) =>
      startTemplateKeywordGenerate(shopId, draftId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: templatePortfolioKeys.generate(draftId) }),
  });
}

function useExecutionWrite<TVariables>(
  mutationFn: (variables: TVariables) => Promise<TemplatePortfolioExecution>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (execution) =>
      queryClient.setQueryData(templatePortfolioKeys.execution(execution.id), execution),
  });
}

export function useExecuteTemplatePortfolioDraft(shopId: string, draftId: string) {
  return useExecutionWrite((expectedVersion: number) =>
    executeTemplatePortfolioDraft(shopId, draftId, expectedVersion),
  );
}

export function useRetryTemplatePortfolioExecutionItem(shopId: string, executionId: string) {
  return useExecutionWrite((itemId: string) =>
    retryTemplatePortfolioExecutionItem(shopId, executionId, itemId),
  );
}

export function useEnableTemplatePortfolioExecution(shopId: string, executionId: string) {
  return useExecutionWrite(() => enableTemplatePortfolioExecution(shopId, executionId));
}

export function useRetryTemplatePortfolioEnableItem(shopId: string, executionId: string) {
  return useExecutionWrite((itemId: string) =>
    retryTemplatePortfolioEnableItem(shopId, executionId, itemId),
  );
}
